import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Paper,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import { useAuth } from "../../../context/AuthContext/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../../../api/axiosInstance";

type DictionaryType = "Category";

export interface DictionaryItem {
  id: string;
  categoryName: string;
}

export default function DictionaryPage() {
  const [selectedTab, setSelectedTab] = useState<DictionaryType>("Category");
  const [items, setItems] = useState<DictionaryItem[]>([]);
  const [newItem, setNewItem] = useState<string>("");
  const [editItem, setEditItem] = useState<DictionaryItem | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { isAdmin, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const fetchItems = useCallback(async () => {
    const response = await api.get(`Dictionary/${selectedTab}`);
    setItems(response.data);
  }, [selectedTab]);

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) {
      navigate("/");
    } else {
      fetchItems();
    }
  }, [selectedTab, isAdmin, isAuthenticated, navigate, fetchItems]);

  const handleAdd = async () => {
    if (!newItem.trim()) return;
    await api.post(`Dictionary/${selectedTab}`, { CategoryName: newItem });
    setNewItem("");
    fetchItems();
  };

  const handleDelete = async (id: string) => {
    await api.delete(`Dictionary/${selectedTab}/${id}`);
    fetchItems();
  };

  const openEditModal = (item: DictionaryItem) => {
    setEditItem(item);
    setModalOpen(true);
  };

  const handleUpdate = async () => {
    if (editItem) {
      await api.put(`Dictionary/${selectedTab}/${editItem.id}`, {
        categoryName: editItem.categoryName,
      });
      setModalOpen(false);
      setEditItem(null);
      fetchItems();
    }
  };

  return (
    <div className="p-4 space-y-6">
      {/* Tabs */}
      <Paper>
        <Tabs value={selectedTab} onChange={(e, val) => setSelectedTab(val)}>
          <Tab label="Categories" value="Category" />
        </Tabs>
      </Paper>

      {/* Add New */}
      <div className="flex gap-2 items-center">
        <TextField
          label={`Add ${selectedTab}`}
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          size="small"
        />
        <Button onClick={handleAdd} variant="contained">
          Add
        </Button>
      </div>

      {/* Table */}
      <Table>
        <TableHead>
          <TableRow>
            {/* <TableCell>ID</TableCell> */}
            <TableCell>Name</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id}>
              {/* <TableCell>{item.id}</TableCell> */}
              <TableCell>{item.categoryName}</TableCell>
              <TableCell align="right">
                <IconButton onClick={() => openEditModal(item)}>
                  <Edit />
                </IconButton>
                <IconButton onClick={() => handleDelete(item.id)}>
                  <Delete />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Edit Modal */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)}>
        <DialogTitle>Edit {selectedTab}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Name"
            fullWidth
            value={editItem?.categoryName || ""}
            onChange={(e) =>
              setEditItem((prev) =>
                prev ? { ...prev, categoryName: e.target.value } : null
              )
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
